import { inject } from '@angular/core';
import { Route, Routes } from '@angular/router';
import {
  SAPHIR_API_BASE_URL,
  SAPHIR_MODULE_MANIFEST,
  SaphirModuleDefinition,
} from '@saphir/module-contract';

import {
  ModuleUnavailable,
  ModuleUnavailableReason,
} from '../../features/module-unavailable/module-unavailable';
import { ModuleRegistryService } from '../../services/module-registry.service';
import { moduleApiBase } from '../http/module-api';
import { StaleBuildService } from '../../services/stale-build.service';
import { licenseGuard } from './license.guard';

// Re-exported for the callers that always found the prefix here.
export { MODULE_ROUTE_PREFIX, moduleIdFromUrl } from './module-url';

/**
 * Turns the resolved registry into router configuration — one route per module
 * that is ready to be opened, to be mounted as children of the `modules` route.
 *
 * A single parameterised route (`:moduleId`) is not enough: `loadChildren`
 * receives no arguments, so it cannot tell which module it is being asked for.
 * Concrete routes are also what makes the router's own caching work — it keeps
 * the resolved children per route, so `loadRoutes()` runs once per module.
 *
 * The routes are installed before the first navigation; see
 * `provideModuleRegistry`. Ids that produce no route here — unknown, disabled,
 * or failed to resolve — fall through to the `:moduleId` route in
 * `app.routes.ts`, which explains itself instead of showing nothing.
 */
export function buildModuleRoutes(registry: ModuleRegistryService): Routes {
  // Every installed module gets a route, audience included. Routes are built
  // once during start-up, before the UMC context says who is signed in, so
  // filtering here would leave staff-only modules unreachable for the very
  // people they are for. Whether the CURRENT viewer may open one is a
  // navigation-time question, and `licenseGuard` is where it is answered.
  return registry.allModules().flatMap((module) => {
    if (module.status !== 'ready') {
      return [];
    }

    const definition = registry.definitionOf(module.id);

    return definition ? [moduleRoute(module.id, definition)] : [];
  });
}

function moduleRoute(id: string, definition: SaphirModuleDefinition): Route {
  return {
    path: id,
    // Resolved by `TranslatedTitleStrategy`, so a module names the browser tab
    // through its own dictionary. Its child routes may override it.
    title: definition.manifest.titleKey,
    // The licence check runs per navigation, not per registration: licences
    // follow the ACTIVE COMPANY, which changes at runtime.
    canActivate: [licenseGuard],
    providers: [
      // Route-scoped, not root: two modules must never see each other's
      // instances of anything they provide.
      ...(definition.providers ?? []),
      { provide: SAPHIR_MODULE_MANIFEST, useValue: definition.manifest },
      // The module's OWN backend, for the cases that build a URL by hand — a
      // download link, an image src. Anything through `HttpClient` needs no
      // base: `apiUrlInterceptor` resolves relative URLs against the same
      // address. Empty for a module without a backend.
      { provide: SAPHIR_API_BASE_URL, useValue: moduleApiBase(id) ?? '' },
    ],
    loadChildren: () => {
      // `loadChildren` runs in injection context; the `.catch` below does not,
      // so the service is taken here, before the import starts.
      const staleBuild = inject(StaleBuildService);

      // `loadRoutes()` is the module's own dynamic import, so it can still fail
      // here — a stale chunk after a redeploy, or a lost connection. Answering
      // with an explanation in place keeps the URL intact and avoids the blank
      // screen a rejected navigation produces. A redeploy is told apart from
      // the rest: "check the connection" would send the person the wrong way
      // when a reload is what fixes it.
      return definition.loadRoutes().catch(async (error: unknown) => {
        console.error(`[modules] Module "${id}" failed to open:`, error);

        const reason: ModuleUnavailableReason = (await staleBuild.report(error))
          ? 'stale'
          : 'failed';

        return [
          { path: '**', component: ModuleUnavailable, data: { moduleId: id, reason } },
        ] satisfies Routes;
      });
    },
  };
}
